/**
 * What the player actually did, counted from the hand records.
 *
 * Two separate things live here and they are deliberately not mixed:
 *
 *   The STATS are facts. VPIP is the share of hands you put money in voluntarily; it
 *   is a count divided by a count and nobody can argue with it. They are computed from
 *   the recorded hands, so they can be recomputed from a replay and checked.
 *
 *   The FINDINGS are opinions — that a stat is outside the band good players fall in,
 *   and that this is costing money. They are labelled as our model's opinion wherever
 *   they are shown, because that is what they are. The one exception is EV loss, which
 *   is measured against the reference model rather than asserted, and even that is a
 *   heuristic and says so.
 */
import type { HandRecord } from "./game";
import type { Street } from "./types";

export interface Stats {
  hands: number;
  /** Voluntarily put money in preflop, excluding the blinds. */
  vpip: number;
  /** Raised or re-raised preflop. */
  pfr: number;
  threeBetChances: number;
  threeBets: number;
  facedThreeBet: number;
  foldedToThreeBet: number;
  sawFlop: number;
  wentToShowdown: number;
  wonAtShowdown: number;
  /** Won the pot having seen the flop, showdown or not. */
  wonWhenSawFlop: number;
  /** Postflop bets and raises, over calls — the aggression factor's two halves. */
  aggressiveActions: number;
  passiveActions: number;
  /** Continuation bets: was the preflop raiser and bet the flop. */
  cbetChances: number;
  cbets: number;
  facedCbet: number;
  foldedToCbet: number;
  /** Chips won or lost, and how much of it the reference model says was avoidable. */
  net: number;
  decisions: number;
  evLost: number;
  evLostByStreet: Record<Street, number>;
  /** Total put in blind, so bb/100 can be reported per big blind rather than per chip. */
  bigBlind: number;
  allIns: number;
}

export function emptyStats(bigBlind = 1): Stats {
  return {
    hands: 0, vpip: 0, pfr: 0, threeBetChances: 0, threeBets: 0,
    facedThreeBet: 0, foldedToThreeBet: 0,
    sawFlop: 0, wentToShowdown: 0, wonAtShowdown: 0, wonWhenSawFlop: 0,
    aggressiveActions: 0, passiveActions: 0,
    cbetChances: 0, cbets: 0, facedCbet: 0, foldedToCbet: 0,
    net: 0, decisions: 0, evLost: 0,
    evLostByStreet: { preflop: 0, flop: 0, turn: 0, river: 0 },
    bigBlind, allIns: 0,
  };
}

/**
 * Fold a hand into a running total.
 *
 * Incremental rather than a pass over the whole history, so a long session does not
 * get slower as it goes and the numbers survive the replay buffer being trimmed.
 */
export function accumulate(s: Stats, hand: HandRecord, seat: number): Stats {
  /**
   * A seat with no chips in front of it at the start of the hand was not dealt in.
   *
   * The earlier guard tested `holes.length > seat`, which is always true — `holes` is
   * built by mapping over every seat, and a seat sitting out appears as `null`, which
   * is also what a dealt-in seat that folded gets. So it excluded nothing, and any
   * caller trusting it would have counted every hand a busted tournament seat sat out
   * in the denominator, deflating that seat's VPIP, PFR and win rate.
   */
  const stack = hand.stacksBefore[seat];
  if (stack === undefined || stack <= 0) return s;

  s.hands++;
  s.bigBlind = hand.rules.bigBlind;
  s.net += hand.result.net[seat] ?? 0;

  const mine = hand.actions.filter((a) => a.seat === seat);
  const preflop = hand.actions.filter((a) => a.street === "preflop");

  /* ------------------------------------------------------------- preflop --- */
  const raiseKinds = new Set(["bet", "raise", "allin"]);
  const myPre = mine.filter((a) => a.street === "preflop");

  // The blinds are posted by the engine, not chosen, so they never appear as actions.
  // Anything here that puts chips in is therefore voluntary by construction.
  if (myPre.some((a) => a.amount > 0)) s.vpip++;
  if (myPre.some((a) => raiseKinds.has(a.action.kind))) s.pfr++;

  /**
   * Three-bet opportunity: somebody raised before this seat's first action, and the
   * seat still had a decision. Counting every hand as an opportunity — which is the
   * easy mistake — makes a normal 3-bet frequency look like a tenth of what it is.
   */
  const firstMine = myPre[0];
  if (firstMine) {
    const before = preflop.slice(0, preflop.indexOf(firstMine));
    const raisesBefore = before.filter((a) => raiseKinds.has(a.action.kind)).length;
    if (raisesBefore === 1) {
      s.threeBetChances++;
      if (raiseKinds.has(firstMine.action.kind)) s.threeBets++;
    }
  }

  /**
   * Facing a three-bet is a reply, and usually not this seat's FIRST action.
   *
   * Keying off `myPre[0]` measured the wrong population entirely: for the player who
   * opened the pot, their first action IS the open, at which point no raise has
   * happened — so the canonical open, three-bet, fold sequence contributed nothing at
   * all, and the only seats ever counted were cold-callers and squeezers. The fix is
   * to find the first action this seat took once two raises were already in.
   */
  for (const mine of myPre) {
    const beforeIt = preflop.slice(0, preflop.indexOf(mine));
    const raises = beforeIt.filter((a) => raiseKinds.has(a.action.kind)).length;
    if (raises >= 2) {
      s.facedThreeBet++;
      if (mine.action.kind === "fold") s.foldedToThreeBet++;
      break;
    }
  }

  const preflopRaiser = lastPreflopRaiser(hand);

  /* ------------------------------------------------------------ postflop --- */
  if (hand.sawFlop.includes(seat)) {
    s.sawFlop++;
    const won = (hand.result.net[seat] ?? 0) > 0;
    if (won) s.wonWhenSawFlop++;
    if (hand.showdownSeats.includes(seat)) {
      s.wentToShowdown++;
      if (won) s.wonAtShowdown++;
    }

    const flop = hand.actions.filter((a) => a.street === "flop");
    /**
     * A continuation bet is a bet into a pot nobody has bet into yet.
     *
     * Without the "nothing bet before it" test, a hand where an opponent donk-bets the
     * flop counted as a c-betting opportunity the raiser never had — dragging c-bet%
     * down — and a RAISE of that donk bet was scored as a c-bet, which it is not.
     * Both directions are wrong, and the c-bet band is something the leak report tells
     * a player to their face.
     */
    const firstAggression = flop.findIndex((a) => raiseKinds.has(a.action.kind));
    if (preflopRaiser === seat) {
      const myFirstFlop = flop.findIndex((a) => a.seat === seat);
      const reachedMeUnbet = myFirstFlop >= 0
        && (firstAggression < 0 || firstAggression >= myFirstFlop);
      if (reachedMeUnbet) {
        s.cbetChances++;
        if (raiseKinds.has(flop[myFirstFlop]!.action.kind)) s.cbets++;
      }
    } else if (preflopRaiser !== null) {
      // The raiser's c-bet is their first flop action, and only if it was a bet into
      // an unbet pot — a raise of somebody else's donk bet is a different decision.
      const raiserFirst = flop.findIndex((a) => a.seat === preflopRaiser);
      const wasCbet = raiserFirst >= 0
        && raiseKinds.has(flop[raiserFirst]!.action.kind)
        && (firstAggression < 0 || firstAggression >= raiserFirst);
      if (wasCbet) {
        const myReply = flop.findIndex((a, i) => a.seat === seat && i > raiserFirst);
        if (myReply >= 0) {
          s.facedCbet++;
          if (flop[myReply]!.action.kind === "fold") s.foldedToCbet++;
        }
      }
    }
  }

  /**
   * All-in is counted by what the seat COMMITTED, not by the label on the action.
   *
   * `legalActions` only offers an `allin` action when the all-in amount exceeds the
   * current bet; a player putting their last chips in for LESS than a call is offered
   * — and takes — a `call`. So counting `kind === "allin"` silently excluded every
   * short call-off, which in a push/fold tournament is most of them, while counting
   * every shove. The stat meant two different things depending on which side of the
   * bet the player was on.
   */
  const committed = mine.reduce((t, a) => t + a.amount, 0);
  const started = hand.stacksBefore[seat] ?? 0;
  if (started > 0 && committed >= started - 1e-9) s.allIns++;

  for (const a of mine) {
    if (a.street === "preflop") continue;
    if (raiseKinds.has(a.action.kind)) s.aggressiveActions++;
    else if (a.action.kind === "call") s.passiveActions++;
  }

  for (const a of mine) {
    if (!a.graded) continue;
    s.decisions++;
    s.evLost += a.graded.evLost;
    s.evLostByStreet[a.street] += a.graded.evLost;
  }

  return s;
}

/** The seat that made the last preflop raise, or null if the pot was never raised. */
function lastPreflopRaiser(hand: HandRecord): number | null {
  let seat: number | null = null;
  for (const a of hand.actions) {
    if (a.street !== "preflop") break;
    if (a.action.kind === "bet" || a.action.kind === "raise" || a.action.kind === "allin") seat = a.seat;
  }
  return seat;
}

/* -------------------------------------------------------------- derived --- */

const pct = (num: number, den: number): number | null => (den > 0 ? (num / den) * 100 : null);

export interface Derived {
  vpip: number | null;
  pfr: number | null;
  /** The gap between them. A large gap is a passive player; a tiny one is a nit. */
  vpipPfrGap: number | null;
  threeBet: number | null;
  foldToThreeBet: number | null;
  wtsd: number | null;
  wsd: number | null;
  wwsf: number | null;
  cbet: number | null;
  foldToCbet: number | null;
  /** Bets and raises per call after the flop. */
  aggressionFactor: number | null;
  bb100: number | null;
  evLostPer100: number | null;
  allInsPer100: number | null;
}

export function derive(s: Stats): Derived {
  return {
    vpip: pct(s.vpip, s.hands),
    pfr: pct(s.pfr, s.hands),
    vpipPfrGap: s.hands > 0 ? ((s.vpip - s.pfr) / s.hands) * 100 : null,
    threeBet: pct(s.threeBets, s.threeBetChances),
    foldToThreeBet: pct(s.foldedToThreeBet, s.facedThreeBet),
    wtsd: pct(s.wentToShowdown, s.sawFlop),
    wsd: pct(s.wonAtShowdown, s.wentToShowdown),
    wwsf: pct(s.wonWhenSawFlop, s.sawFlop),
    cbet: pct(s.cbets, s.cbetChances),
    foldToCbet: pct(s.foldedToCbet, s.facedCbet),
    aggressionFactor: s.passiveActions > 0 ? s.aggressiveActions / s.passiveActions
      : s.aggressiveActions > 0 ? Infinity : null,
    bb100: s.hands > 0 && s.bigBlind > 0 ? (s.net / s.bigBlind / s.hands) * 100 : null,
    evLostPer100: s.hands > 0 && s.bigBlind > 0 ? (s.evLost / s.bigBlind / s.hands) * 100 : null,
    allInsPer100: pct(s.allIns, s.hands),
  };
}

/* --------------------------------------------------------------- leaks --- */

export type Severity = "watch" | "leak" | "major";

export interface Finding {
  id: string;
  title: string;
  /** What the number is, and what it usually is. */
  detail: string;
  /** What to do about it. One sentence, actionable. */
  advice: string;
  severity: Severity;
  /** The stat and its value, so the UI can show the evidence beside the claim. */
  stat: string;
  value: number;
  /** The range this model considers healthy. */
  band: [number, number];
  /** How many observations the finding rests on. Small samples are shown as such. */
  sample: number;
}

interface Rule {
  id: string;
  stat: keyof Derived;
  band: [number, number];
  /** Minimum observations before the rule is allowed to say anything. */
  sample: (s: Stats) => number;
  minSample: number;
  low?: { title: string; detail: (v: number) => string; advice: string };
  high?: { title: string; detail: (v: number) => string; advice: string };
}

/**
 * The bands.
 *
 * These are the ranges winning small-stakes players fall in, not solver outputs, and
 * the copy says so. A player outside one of them is not necessarily wrong — a
 * deliberately loose-aggressive style sits outside several — which is why the findings
 * are phrased as "this is unusual and here is what it usually costs", not as verdicts.
 */
const RULES: Rule[] = [
  {
    id: "vpip", stat: "vpip", band: [17, 30],
    sample: (s) => s.hands, minSample: 40,
    low: {
      title: "Playing too few hands",
      detail: (v) => `You enter ${v.toFixed(0)}% of pots; winning players at a full table are usually 17-30%.`,
      advice: "Open more from the button and the cutoff — suited connectors and small pairs are profitable there and nowhere else.",
    },
    high: {
      title: "Playing too many hands",
      detail: (v) => `You enter ${v.toFixed(0)}% of pots; winning players at a full table are usually 17-30%.`,
      advice: "The cheapest fix in poker: fold your worst hands from the first three seats. Nothing else changes.",
    },
  },
  {
    id: "gap", stat: "vpipPfrGap", band: [0, 9],
    sample: (s) => s.hands, minSample: 40,
    high: {
      title: "Calling when you should be raising",
      detail: (v) => `You call ${v.toFixed(0)} percentage points more often than you raise before the flop. Under 9 is where solid players sit.`,
      advice: "If a hand is good enough to put money in with, it is usually good enough to raise. Limping gives up the pot you could have won right there.",
    },
  },
  {
    id: "threebet", stat: "threeBet", band: [4, 12],
    sample: (s) => s.threeBetChances, minSample: 25,
    low: {
      title: "Not three-betting enough",
      detail: (v) => `You re-raise ${v.toFixed(0)}% of the time facing a raise; 4-12% is normal.`,
      advice: "Re-raise your strongest hands and a few suited hands with an ace or king in them. A range that only ever has aces is one everyone folds to.",
    },
    high: {
      title: "Three-betting too often",
      detail: (v) => `You re-raise ${v.toFixed(0)}% of the time facing a raise; 4-12% is normal.`,
      advice: "Three-betting light is only profitable against players who fold. Against the ones who call, it just builds a pot you are behind in.",
    },
  },
  {
    id: "foldto3b", stat: "foldToThreeBet", band: [40, 70],
    sample: (s) => s.facedThreeBet, minSample: 20,
    high: {
      title: "Folding too much to re-raises",
      detail: (v) => `You fold ${v.toFixed(0)}% of the time facing a three-bet; over 70% and anyone paying attention can re-raise you with anything.`,
      advice: "Pick a few hands to continue with — pairs and suited broadways — rather than folding everything but the top.",
    },
  },
  {
    id: "wtsd", stat: "wtsd", band: [22, 32],
    sample: (s) => s.sawFlop, minSample: 40,
    low: {
      title: "Giving up too early after the flop",
      detail: (v) => `You reach showdown ${v.toFixed(0)}% of the time you see a flop; 22-32% is normal.`,
      advice: "Folding every time you miss makes you easy to bet at. Keep the hands that can improve, and the ones that beat a bluff.",
    },
    high: {
      title: "Calling too far with weak hands",
      detail: (v) => `You reach showdown ${v.toFixed(0)}% of the time you see a flop; 22-32% is normal.`,
      advice: "Second pair is not a hand you have to see the river with. Decide on the flop whether it can stand three bets.",
    },
  },
  {
    id: "af", stat: "aggressionFactor", band: [1.2, 4],
    sample: (s) => s.aggressiveActions + s.passiveActions, minSample: 40,
    low: {
      title: "Too passive after the flop",
      detail: (v) => `You call ${(1 / Math.max(v, 0.01)).toFixed(1)} times for every bet or raise. Winning players bet and raise more than they call.`,
      advice: "When you have the best hand, bet it. Calling with a good hand wins the pot you already had and none of the one you could have built.",
    },
    high: {
      title: "Betting and raising too much",
      detail: (v) => `You bet or raise ${v.toFixed(1)} times for every call after the flop, which is a lot.`,
      advice: "Bluffs need someone who folds. Check the hands that are ahead but cannot stand a raise, and let the players who cannot fold pay you.",
    },
  },
  {
    id: "cbet", stat: "cbet", band: [45, 75],
    sample: (s) => s.cbetChances, minSample: 20,
    high: {
      title: "Continuation-betting every flop",
      detail: (v) => `You bet ${v.toFixed(0)}% of flops after raising before them; 45-75% is the usual range.`,
      advice: "Bet the flops that favour your raising range — high cards, few draws — and check the ones that favour a caller.",
    },
    low: {
      title: "Not following up on your raises",
      detail: (v) => `You bet ${v.toFixed(0)}% of flops after raising before them; 45-75% is the usual range.`,
      advice: "You raised because you had the better range. On most flops you still do, and betting is how that gets paid.",
    },
  },
  {
    id: "foldtocbet", stat: "foldToCbet", band: [35, 60],
    sample: (s) => s.facedCbet, minSample: 20,
    high: {
      title: "Folding too often to the flop bet",
      detail: (v) => `You fold ${v.toFixed(0)}% of the time facing a flop continuation bet; over 60% invites a bet on every flop.`,
      advice: "Keep your pairs and your draws. Folding everything that missed is exactly what the bet is hoping for.",
    },
  },
];

/**
 * Turn stats into findings, worst first.
 *
 * A rule stays silent below its minimum sample. This matters more than it looks: three-
 * bet percentage over four opportunities is either 0% or 33%, and a report that opens
 * with "you never three-bet" after twenty hands is a report nobody trusts again.
 */
export function findings(s: Stats): Finding[] {
  const d = derive(s);
  const out: Finding[] = [];

  for (const r of RULES) {
    const value = d[r.stat];
    if (value === null || !Number.isFinite(value)) continue;
    const sample = r.sample(s);
    if (sample < r.minSample) continue;

    const [lo, hi] = r.band;
    const side = value < lo ? r.low : value > hi ? r.high : undefined;
    if (!side) continue;

    const distance = value < lo ? (lo - value) / Math.max(1, lo) : (value - hi) / Math.max(1, hi);
    out.push({
      id: r.id,
      title: side.title,
      detail: side.detail(value),
      advice: side.advice,
      severity: distance > 0.6 ? "major" : distance > 0.25 ? "leak" : "watch",
      stat: r.stat,
      value,
      band: r.band,
      sample,
    });
  }

  /**
   * Where the money actually went, which is the one finding not derived from a band.
   * Reported only when there is enough of it to point somewhere, and always described
   * as this model's estimate.
   */
  const streets = Object.entries(s.evLostByStreet) as [Street, number][];
  const worst = streets.sort((a, b) => b[1] - a[1])[0];
  if (worst && s.decisions >= 40 && s.bigBlind > 0 && worst[1] / s.bigBlind > s.hands * 0.02) {
    const share = s.evLost > 0 ? (worst[1] / s.evLost) * 100 : 0;
    out.push({
      id: `evloss_${worst[0]}`,
      title: `Most of the avoidable loss is ${worst[0] === "preflop" ? "before the flop" : `on the ${worst[0]}`}`,
      detail: `Our model puts ${(worst[1] / s.bigBlind / s.hands * 100).toFixed(1)} big blinds per 100 hands of avoidable loss ` +
        `${worst[0] === "preflop" ? "before the flop" : `on the ${worst[0]}`} — ${share.toFixed(0)}% of the total. ` +
        `This is our model's estimate, not a solve.`,
      advice: worst[0] === "preflop"
        ? "Preflop is the cheapest street to fix, because the decision is the same every time and you can learn it away from the table."
        : `Replay your ${worst[0]} decisions in the hand history and look at what the model preferred.`,
      severity: "leak",
      stat: "evLostPer100",
      value: (worst[1] / s.bigBlind / s.hands) * 100,
      band: [0, 0],
      sample: s.decisions,
    });
  }

  const rank: Record<Severity, number> = { major: 0, leak: 1, watch: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/**
 * What the player is already doing well.
 *
 * Every report gets one of these sections, and it is not decoration. Knowing which
 * parts of your game are sound is what tells you where NOT to spend the next hour —
 * scarcer and more useful information than another leak.
 *
 * It is built from the SAME bands as the findings, read the other way: a stat sitting
 * comfortably inside its healthy range, on enough hands to mean it. That symmetry is
 * the honesty guarantee. A separate, softer rule set for praise would drift into
 * flattery the moment someone wanted the section to look fuller, and a report that
 * flatters loses the authority of the half that criticises.
 */
export interface Strength {
  id: string;
  title: string;
  /** The number, and the range it sits in. */
  detail: string;
  stat: string;
  value: number;
  band: [number, number];
  sample: number;
}

/**
 * How far inside a band a stat must sit to count.
 *
 * A value one decimal off the edge is not a demonstrated strength, it is a value that
 * could cross the line next session. Ten per cent of the band's width in from each
 * edge keeps the claim stable enough to be worth making.
 */
const STRENGTH_MARGIN = 0.1;

export function strengths(s: Stats): Strength[] {
  const d = derive(s);
  const out: Strength[] = [];

  for (const r of RULES) {
    const value = d[r.stat];
    if (value === null || !Number.isFinite(value)) continue;
    const sample = r.sample(s);
    if (sample < r.minSample) continue;

    const [lo, hi] = r.band;
    const inset = (hi - lo) * STRENGTH_MARGIN;
    if (value < lo + inset || value > hi - inset) continue;

    out.push({
      id: `ok_${r.id}`,
      title: TITLES[r.id] ?? `${r.id} is where it should be`,
      detail: `${value.toFixed(1)} sits inside the ${lo}–${hi} range winning players fall in, ` +
        `across ${sample} observations.`,
      stat: r.stat,
      value,
      band: r.band,
      sample,
    });
  }

  /** Strongest evidence first: the claim resting on the most observations. */
  return out.sort((a, b) => b.sample - a.sample);
}

/**
 * Plain-language names for the stats a player might be doing well at.
 *
 * Written as things a person DOES rather than as statistic names, because "your
 * three-bet frequency is within band" is a sentence about a spreadsheet and "you
 * three-bet about as often as you should" is a sentence about poker.
 */
const TITLES: Record<string, string> = {
  vpip:       "You are entering about the right number of pots",
  gap:        "Your raising and your calling are in step",
  threebet:   "You three-bet about as often as you should",
  foldto3b:   "You defend against three-bets sensibly",
  wtsd:       "You are getting to showdown about as often as you should",
  af:         "Your aggression is well balanced",
  cbet:       "Your continuation betting is well judged",
  foldtocbet: "You handle continuation bets well",
};
